import Button from 'components/base/Button';
import { Col, Form, Row, Modal } from 'react-bootstrap';
import ModalForm from 'components/forms/ModalForm';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { FormProps } from 'data/from-data/interface/modal-form-props.interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  JobTypeDto,
  UPDATE_JOB_TYPE,
  CREATE_JOB_TYPE
} from 'services/api/jobTypes';
import toast from 'react-hot-toast';

export class JobTypeFormData {
  [key: string]: FormProps;

  name: FormProps = {
    name: 'name',
    label: 'Name',
    type: 'text',
    placeholder: 'Name',
    required: true
  };
  description: FormProps = {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Description',
    required: false
  };
}

interface JobTypeModalProps {
  show: boolean;
  handleClose: () => void;
  isEditing?: boolean;
  jobType?: any;
}

const JobTypeModal = ({
  show,
  handleClose,
  isEditing = false,
  jobType
}: JobTypeModalProps) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createJobTypeDto, setJobTypeDto] = useState<JobTypeFormData>(
    new JobTypeFormData()
  );
  const [isLoading, setLoading] = useState<boolean>(false);

  const query = useQueryClient();

  useEffect(() => {
    setJobTypeDto(prevData => {
      const updatedData = Object.keys(prevData).reduce((acc: any, key) => {
        acc[key] = {
          ...prevData[key],
          value: jobType?.[key] || prevData[key].value
        };
        return acc;
      }, {});
      return updatedData;
    });
  }, [jobType]);

  const updateJobTypeAction = useMutation('editJobType', UPDATE_JOB_TYPE, {
    onError: (error: any) => {
      setLoading(false);
      toast.error(error.response?.data?.message || 'Failed to update job type');
    },
    onSuccess: () => {
      setLoading(false);
      query.invalidateQueries('getJobTypes');
      query.invalidateQueries([
        'getJobType',
        (jobType as any)?.id || jobType?._id
      ]);
      toast.success('Job type updated successfully');
      handleClose();
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const createJobTypeAction = useMutation('createJobType', CREATE_JOB_TYPE, {
    onError: (error: any) => {
      setLoading(false);
      toast.error(error.response?.data?.message || 'Failed to create job type');
    },
    onSuccess: () => {
      setLoading(false);
      query.invalidateQueries('getJobTypes');
      toast.success('Job type created successfully');
      handleClose();
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!createJobTypeDto.name.value) {
      event.stopPropagation();
      setValidated(true);
      toast.error('Please enter a name for the job type');
    } else {
      handleClose();
      return runFormSubmissionAction(event);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setJobTypeDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    try {
      const {
        name: { value: name },
        description: { value: description }
      } = createJobTypeDto;

      const payload: JobTypeDto = {
        ...createJobTypeDto,
        name,
        description
      };

      if (isEditing) {
        await updateJobTypeAction.mutateAsync({
          jobTypeId: (jobType as any)?.id || jobType?._id,
          data: payload
        });
      } else {
        await createJobTypeAction.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Error with job type:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} job type`);
    }
  };

  const renderFormFields = () => {
    return Object.entries(createJobTypeDto).map(([key, formProps]) => (
      <Col md="12" key={key}>
        <ModalForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };

  return (
    <Modal show={show} onHide={handleClose} className="p-0" centered>
      <Modal.Header className="border-200 p-4">
        <h5 className="modal-title text-1000 fs-6 lh-sm">
          {isEditing ? 'Edit Job Type' : 'Create New Job Type'}
        </h5>
        <Button className="p-1" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-9" />
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-4 pb-2 px-4 mb-5">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row className="g-3 mb-8">{renderFormFields()}</Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
        <Button
          variant="phoenix-primary"
          size="sm"
          className="px-4 fs-10 my-0"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="px-9 fs-9 my-0"
          disabled={isLoading}
          type="submit"
          onClick={handleSubmit}
        >
          {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JobTypeModal;
